import { useRef, useState } from 'react';
import { Button } from '@/ui';
import { usersApi } from '@/api';
import type { User } from '@/types';
import { StepLayout } from '../StepLayout';

interface LogoStepProps {
  profile: User;
  onSaved: (user: User) => void;
  onSkip: () => void;
}

const ACCEPT = '.png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml';
const MAX_BYTES = 5 * 1024 * 1024;

export function LogoStep({ profile, onSaved, onSkip }: LogoStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  // Object URL for the locally-selected file preview. Revoked when replaced
  // so we don't leak blob URLs across selections.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentLogo = profile.logoUrl;

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError('Logo must be 5 MB or smaller.');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    setPendingFile(file);
    setError(null);
  };

  const handleNext = async () => {
    // No new file? Just advance — they're keeping whatever is on the account.
    if (!pendingFile) {
      onSkip();
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const updated = await usersApi.uploadLogo(pendingFile);
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to upload logo.';
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const displayedLogo = previewUrl ?? currentLogo;

  return (
    <StepLayout
      title="Upload your logo"
      subtitle="Shown on PDF reports, the public client portal, and your booking page. PNG, JPG, WebP, or SVG up to 5 MB."
      onPrimary={handleNext}
      primaryLabel={pendingFile ? 'Upload & continue' : 'Continue'}
      primaryLoading={uploading}
      onSecondary={onSkip}
      secondaryLabel="Skip for now"
      error={error}
    >
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="h-32 w-32 rounded-2xl border-2 border-dashed border-border-strong bg-surface-2 flex items-center justify-center overflow-hidden">
          {displayedLogo ? (
            <img
              src={displayedLogo}
              alt="Logo preview"
              className="h-full w-full object-contain"
            />
          ) : (
            <span className="text-caption text-ink-subtle text-center px-2">No logo yet</span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          onChange={handlePick}
          className="hidden"
        />
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
            {currentLogo || pendingFile ? 'Choose a different file' : 'Choose a file'}
          </Button>
        </div>
      </div>
    </StepLayout>
  );
}
